// ============================================================
// fondos/efectos/bokeh.js — «Bokeh»: círculos de luz desenfocados que flotan y respiran (efecto de
// LIENZO, SUAVE)
//
// Portado de `vendor/bg/bokeh-bg.js` en la F2b del P3: un sprite con degradado radial pintado una sola
// vez y copiado con `drawImage`. Es SUAVE (`nitidez`): su dibujo ya es desenfocado, así que no se sube a
// la resolución de la pantalla (retina) — `fondos/lienzo.js` lo deja a 1 px de lienzo por px CSS.
//
// Rehecho en la F3 (E10 y legibilidad; el anillo del botón fallaba en las tres polaridades, 2,3-2,8):
//   · sin `multiply`: sobre claro los círculos se multiplicaban unos con otros y oscurecían la sección;
//     ahora van en `source-over` sobre claro y en `screen` sobre oscuro (se suman como luz);
//   · el alfa de cada círculo sale del tope del catálogo (`alfaMax`) y BAJA con la densidad: con más
//     círculos, cada uno más tenue, así que subir la densidad no tapa el texto (`alfaPorDensidad`);
//   · la SUAVIDAD (la clave `blur`, 0-100) es el sprite: a 0, un DISCO con borde (el bokeh de una
//     lente: relleno plano, un aro algo más brillante y el filo suave); al subir, el aro se funde y el
//     disco se vuelve un resplandor sin filo, que crece un poco (×1,2 a 100), como las manchas de los
//     fluidos. Nada de `filter` sobre el lienzo (E10: dejaba viñeta en los bordes y bajaba de 30 a
//     17,9 fps a 100 px).
//
// Reanimado el 29-09 (§3.1 del traspaso: a opacidad 50, con el resplandor de siempre y una deriva de
// 1,5 px/s se veía como una bruma más): discos con borde, que derivan a la vista y titilan sin
// apagarse del todo.
//
// MÓDULO PURO: ES2019 sin `?.` ni `??`, sin DOM, sin reloj y sin estado de módulo.
// ============================================================

import { pasos, rgbDe, interpolar } from './comun.js';
import { crecePorSuavidad } from './manchas.js';

/** Lado (px) del sprite del círculo de luz. */
const SPRITE = 128;

/** Lo más bajo a lo que titila un disco (fracción de su alfa). */
const TITILA_MIN = 0.35;

/** Cuántos círculos hay a la densidad por defecto (5): por encima, cada uno se atenúa. */
const CIRCULOS_REFERENCIA = 20;

/**
 * El alfa de cada círculo con `n` círculos: el tope hasta la densidad por defecto y, por encima, lo
 * justo para que el conjunto no pinte más que con `CIRCULOS_REFERENCIA`.
 */
export function alfaPorDensidad(tope, n) {
    return tope * Math.min(1, CIRCULOS_REFERENCIA / Math.max(1, n));
}

/** El disco (suavidad 0): relleno, aro y filo, `[radio 0-1, alfa]` de dentro afuera. */
const DISCO = [[0, 0.62], [0.72, 0.66], [0.86, 1], [0.93, 0.78], [1, 0]];
/** El resplandor sin filo (suavidad 100): alfa 1 en el centro y una cola larga. */
function _resplandor(r) { return Math.pow(1 - r, 2.5); }

/**
 * Las paradas `[radio 0-1, alfa]` del sprite con la suavidad `suavidad` (0-100): a 0, el DISCO; a 100,
 * el resplandor; en medio, la mezcla de los dos en los mismos radios (el aro se va fundiendo).
 */
export function paradasDelSprite(suavidad) {
    const s = (typeof suavidad === 'number' && suavidad > 0) ? Math.min(suavidad, 100) / 100 : 0;
    const radios = [0, 0.2, 0.4, 0.6, 0.72, 0.8, 0.86, 0.93, 1];
    return radios.map(function (r) {
        let d = 0;
        for (let i = 1; i < DISCO.length; i++) {
            if (r <= DISCO[i][0]) {
                const a = DISCO[i - 1], b = DISCO[i];
                d = a[1] + (b[1] - a[1]) * (r - a[0]) / (b[0] - a[0]);
                break;
            }
        }
        return [r, Math.round((d * (1 - s) + _resplandor(r) * s) * 10000) / 10000];
    });
}

function sprite(crearLienzo, rgb, suavidad) {
    const s = SPRITE;
    const lienzo = crearLienzo(s, s);
    const c = lienzo.getContext('2d');
    const grad = c.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    paradasDelSprite(suavidad).forEach(function (pa) {
        grad.addColorStop(pa[0], `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${pa[1]})`);
    });
    c.fillStyle = grad;
    c.beginPath();
    c.arc(s / 2, s / 2, s / 2, 0, Math.PI * 2);
    c.fill();
    return lienzo;
}

export default {
    nitidez: 'suave',

    crear(ctx, e) {
        // Densidad 1-10 → 8-35 círculos (5-20 en táctil).
        const n = Math.round(interpolar(e.params.density, 5, e.tactil ? 5 : 8, e.tactil ? 20 : 35));
        const crece = crecePorSuavidad(e.params.blur);
        const circulos = [];
        for (let i = 0; i < n; i++) {
            circulos.push({
                x: e.azar(),
                y: e.azar(),
                vx: (e.azar() - 0.5) * 0.9,
                vy: (e.azar() - 0.5) * 0.9 - 0.12,
                radius: (10 + e.azar() * 80) * crece,
                alphaOffset: e.azar() * Math.PI * 2,
                alphaSpeed: 0.5 + e.azar() * 1.5,
            });
        }
        const rgb = rgbDe(e.c1);
        return { w: e.w, h: e.h, oscuro: e.oscuro, alfa: alfaPorDensidad(e.alfa, n), circulos: circulos,
                 sprite: sprite(e.crearLienzo, rgb, e.params.blur) };
    },

    redimensionar(s, w, h) {
        s.w = w;
        s.h = h;
    },

    dibujar(s, ctx, t, dt) {
        const k = pasos(dt);
        const W = s.w;
        const H = s.h;
        const time = t * 0.001;

        // Sobre oscuro las luces se suman (`screen`); sobre claro se superponen sin oscurecerse.
        ctx.globalCompositeOperation = s.oscuro ? 'screen' : 'source-over';
        s.circulos.forEach(function (circle) {
            if (k > 0) {
                circle.x += circle.vx * k / W;
                circle.y += circle.vy * k / H;
                if (circle.x < -0.1 || circle.x > 1.1) circle.vx *= -1;
                if (circle.y < -0.1 || circle.y > 1.1) circle.vy *= -1;
            }
            const px = circle.x * W;
            const py = circle.y * H;
            // Titilan despacio, sin llegar a apagarse.
            const flicker = TITILA_MIN + (1 - TITILA_MIN) * (Math.sin(time * circle.alphaSpeed + circle.alphaOffset) * 0.5 + 0.5);
            ctx.globalAlpha = s.alfa * flicker;
            const d = circle.radius * 2;
            ctx.drawImage(s.sprite, px - circle.radius, py - circle.radius, d, d);
        });
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
    },
};
